import React, { useState, useEffect } from 'react';
import { FaFileSignature, FaSpinner, FaCheck, FaExclamationCircle, FaSync, FaPlus } from 'react-icons/fa';
import { adminService } from '../../services/adminService';

// Customer-detail "Consents & Agreements" section — surfaces the immutable
// CustomerConsents audit trail for a given customer. Each row records the
// docKey + version + ackedAt + IP + user agent at the time of acknowledgement.
//
// Used by CustomerDetailsModal. Lazy-loaded on first render (no network on
// modal open until this section mounts).
//
// Supports two read-only views (timeline + grouped-by-doc) and one write
// operation (record an offline consent on behalf of the customer — e.g. when
// a paper/voice ack came in via support).

const DOC_LABELS = {
  cis: 'Critical Information Summary',
  kfs: 'Key Facts Sheet',
  terms: 'Terms & Conditions',
  privacy: 'Privacy Policy',
  aup: 'Acceptable Use Policy',
  ddsa: 'Direct Debit Service Agreement',
  fvp: 'Family Violence Policy',
  complaints: 'Complaints Handling Policy',
  hardship: 'Financial Hardship Policy',
  'marketing-optin': 'Marketing communications opt-in',
};

const METHOD_LABELS = {
  'web-signup': '🌐 Web signup form',
  'admin-recorded': '👤 Admin recorded',
  'voice': '📞 Voice (recorded)',
  'in-person': '🏢 In person',
  'email': '✉️ Email confirmation',
};

const ConsentsSection = ({ customer }) => {
  const [loading, setLoading] = useState(true);
  const [consents, setConsents] = useState([]);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState('timeline'); // 'timeline' | 'doc'
  const [recordingNew, setRecordingNew] = useState(false);
  const [newConsentForm, setNewConsentForm] = useState({ docKey: 'cis', version: 'v1.1', method: 'voice', note: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    if (!customer?.UID) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getCustomerConsents({ customerId: customer.UID });
      if (res?.consents) setConsents(res.consents);
      else if (res?.error) setError(res.error);
    } catch (err) {
      setError(err.message || 'Failed to load consents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customer?.UID]);

  const handleRecordConsent = async () => {
    if (!newConsentForm.docKey || !newConsentForm.version) return;
    setSubmitting(true);
    try {
      const res = await adminService.recordAdminConsent({
        customerId: customer.UID,
        docKey: newConsentForm.docKey,
        version: newConsentForm.version,
        method: newConsentForm.method,
        note: newConsentForm.note,
      });
      if (res?.success) {
        setRecordingNew(false);
        setNewConsentForm({ docKey: 'cis', version: 'v1.1', method: 'voice', note: '' });
        await load();
      } else {
        setError(res?.error || 'Failed to record consent');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Group consents by document key — latest version of each first
  const groupedByDoc = (() => {
    const byDoc = {};
    consents.forEach(c => {
      if (!byDoc[c.docKey]) byDoc[c.docKey] = [];
      byDoc[c.docKey].push(c);
    });
    Object.values(byDoc).forEach(list => list.sort((a, b) => (b.ackedAt || '').localeCompare(a.ackedAt || '')));
    return byDoc;
  })();

  return (
    <div className="mt-6 pt-6 border-t border-[#3c3836]">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-[#ebdbb2] flex items-center gap-2">
          <FaFileSignature className="text-[#fabd2f]" /> Consents &amp; Agreements
          <span className="text-xs font-normal text-[#928374]">({consents.length} record{consents.length !== 1 ? 's' : ''})</span>
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#3c3836] rounded text-xs">
            <button
              onClick={() => setGroupBy('timeline')}
              className={`px-3 py-1 rounded-l ${groupBy === 'timeline' ? 'bg-[#fabd2f] text-[#1d2021] font-bold' : 'text-[#bdae93] hover:text-white'}`}
            >Timeline</button>
            <button
              onClick={() => setGroupBy('doc')}
              className={`px-3 py-1 rounded-r ${groupBy === 'doc' ? 'bg-[#fabd2f] text-[#1d2021] font-bold' : 'text-[#bdae93] hover:text-white'}`}
            >By Document</button>
          </div>
          <button
            onClick={load}
            disabled={loading}
            title="Reload"
            className="p-2 text-[#bdae93] hover:text-white hover:bg-[#3c3836] rounded"
          >
            <FaSync className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setRecordingNew(!recordingNew)}
            className="px-3 py-1 text-xs bg-[#458588] text-white rounded hover:bg-[#5a9c9f] flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Record Consent
          </button>
        </div>
      </div>

      {/* Record-new form */}
      {recordingNew && (
        <div className="mb-4 p-4 bg-[#3c3836] rounded-lg space-y-3">
          <p className="text-xs text-[#bdae93]">
            Record an offline consent received via phone, in-person, or email. The audit row will be tagged
            with the admin user and method.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#928374] uppercase">Document</label>
              <select
                value={newConsentForm.docKey}
                onChange={(e) => setNewConsentForm({ ...newConsentForm, docKey: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] text-sm focus:ring-1 focus:ring-[#fabd2f] focus:outline-none"
              >
                {Object.entries(DOC_LABELS).map(([k, v]) => <option key={k} value={k}>{v} ({k})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#928374] uppercase">Version</label>
              <input
                value={newConsentForm.version}
                onChange={(e) => setNewConsentForm({ ...newConsentForm, version: e.target.value })}
                placeholder="v1.1"
                className="w-full mt-1 px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] text-sm font-mono focus:ring-1 focus:ring-[#fabd2f] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-[#928374] uppercase">Method</label>
              <select
                value={newConsentForm.method}
                onChange={(e) => setNewConsentForm({ ...newConsentForm, method: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] text-sm focus:ring-1 focus:ring-[#fabd2f] focus:outline-none"
              >
                {Object.entries(METHOD_LABELS).filter(([k]) => k !== 'web-signup').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#928374] uppercase">Note (optional)</label>
              <input
                value={newConsentForm.note}
                onChange={(e) => setNewConsentForm({ ...newConsentForm, note: e.target.value })}
                placeholder="e.g. confirmed on phone call 19/05/2026"
                className="w-full mt-1 px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] text-sm focus:ring-1 focus:ring-[#fabd2f] focus:outline-none"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRecordConsent}
              disabled={submitting || !newConsentForm.docKey || !newConsentForm.version}
              className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded font-semibold text-sm hover:bg-[#fe8019] disabled:opacity-50"
            >
              {submitting ? <FaSpinner className="animate-spin" /> : 'Record'}
            </button>
            <button
              onClick={() => setRecordingNew(false)}
              className="px-4 py-2 border border-[#504945] text-[#ebdbb2] rounded text-sm hover:bg-[#3c3836]"
            >Cancel</button>
          </div>
        </div>
      )}

      {/* Body */}
      {loading && (
        <div className="text-center py-8 text-[#928374]"><FaSpinner className="inline animate-spin mr-2" /> Loading consents…</div>
      )}

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500/50 rounded text-red-300 text-sm flex items-start gap-2">
          <FaExclamationCircle className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && consents.length === 0 && (
        <div className="p-6 bg-[#3c3836] rounded text-center text-[#928374] text-sm">
          No consent records on file for this customer.<br />
          <span className="text-xs">(They may have signed up before the consent-capture system shipped, or via a legacy path. Use "Record Consent" above to add an offline consent.)</span>
        </div>
      )}

      {/* Timeline view */}
      {!loading && !error && consents.length > 0 && groupBy === 'timeline' && (
        <div className="space-y-2">
          {consents.map((c, i) => (
            <div key={`${c.consentTs}-${i}`} className="p-3 bg-[#3c3836] rounded-lg border border-[#504945]">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FaCheck className="text-[#b8bb26] text-xs flex-shrink-0" />
                    <span className="text-[#ebdbb2] font-semibold text-sm">{DOC_LABELS[c.docKey] || c.docKey}</span>
                    <span className="text-xs font-mono text-[#fabd2f] bg-[#1d2021] px-2 py-0.5 rounded">{c.version}</span>
                  </div>
                  <div className="text-xs text-[#928374] mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5">
                    <div>Acked: <span className="text-[#bdae93]">{c.ackedAt ? new Date(c.ackedAt).toLocaleString() : '—'}</span></div>
                    <div>Method: <span className="text-[#bdae93]">{METHOD_LABELS[c.method] || c.method}</span></div>
                    <div>IP: <span className="text-[#bdae93] font-mono">{c.ipAddress || '—'}</span></div>
                    <div className="truncate" title={c.userAgent}>UA: <span className="text-[#bdae93] font-mono">{(c.userAgent || '—').slice(0, 40)}{(c.userAgent || '').length > 40 ? '…' : ''}</span></div>
                    {c.adminNote && <div className="col-span-2">Note: <span className="text-[#bdae93]">{c.adminNote}</span></div>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grouped-by-document view */}
      {!loading && !error && consents.length > 0 && groupBy === 'doc' && (
        <div className="space-y-3">
          {Object.entries(groupedByDoc).map(([docKey, records]) => (
            <div key={docKey} className="bg-[#3c3836] rounded-lg border border-[#504945] overflow-hidden">
              <div className="px-4 py-2 bg-[#1d2021] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaCheck className="text-[#b8bb26]" />
                  <span className="text-[#ebdbb2] font-semibold">{DOC_LABELS[docKey] || docKey}</span>
                </div>
                <span className="text-xs text-[#928374]">{records.length} record{records.length !== 1 ? 's' : ''} · latest: <span className="text-[#fabd2f] font-mono">{records[0].version}</span></span>
              </div>
              <div className="divide-y divide-[#504945]">
                {records.map((c, i) => (
                  <div key={`${c.consentTs}-${i}`} className="px-4 py-2 text-xs grid grid-cols-3 gap-2">
                    <div className="font-mono text-[#fabd2f]">{c.version}</div>
                    <div className="text-[#bdae93]">{c.ackedAt ? new Date(c.ackedAt).toLocaleString() : '—'}</div>
                    <div className="text-[#928374]">{METHOD_LABELS[c.method] || c.method}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConsentsSection;
